import { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const industries = [
  {
    name: 'Manufacturing',
    image:
      'https://ik.imagekit.io/7wqj9br0b/PTKCS/Services/Industries/Manufacturing%20industry.webp',
    description:
      'PT KCS delivers tailored AI solutions for manufacturers, helping optimize processes, improve quality, and increase operational efficiency.',
    href: '/industries/industrial_manufacturing',
  },
  {
    name: 'Retail',
    image:
      'https://ik.imagekit.io/7wqj9br0b/PTKCS/Services/Industries/retail%20industry.webp',
    description:
      'PT KCS helps retailers use AI to understand customer needs, improve inventory decisions, and create more relevant shopping experiences.',
    href: '/contact',
  },
  {
    name: 'Education',
    image:
      'https://ik.imagekit.io/7wqj9br0b/PTKCS/Services/Industries/Education.webp',
    description:
      'PT KCS supports educators with AI tools for personalized learning, faster feedback, and clearer insight into student progress.',
    href: '/contact',
  },
  {
    name: 'Healthcare',
    image:
      'https://ik.imagekit.io/7wqj9br0b/PTKCS/Services/Industries/Healthcare%20industry.webp',
    description:
      'PT KCS helps healthcare teams streamline workflows and make trusted information more accessible, supporting better-informed patient care.',
    href: '/contact',
  },
  {
    name: 'Wholesale & Distribution',
    image:
      'https://ik.imagekit.io/7wqj9br0b/PTKCS/Services/Industries/wholesale%20&%20distribution.webp',
    description:
      'PT KCS helps wholesalers and distributors improve demand planning, inventory visibility, and supply chain operations with practical AI.',
    href: '/contact',
  },
  {
    name: 'Building Materials',
    image:
      'https://ik.imagekit.io/7wqj9br0b/PTKCS/Services/Industries/building%20materials.webp',
    description:
      'PT KCS helps building materials businesses apply AI to improve forecasting, streamline operations, and make more informed decisions.',
    href: '/industries/construction',
  },
  {
    name: 'E-commerce',
    image:
      'https://ik.imagekit.io/7wqj9br0b/PTKCS/Services/Industries/E%20Commerce%20industry.webp',
    description:
      'PT KCS helps e-commerce businesses personalize customer experiences, automate workflows, and make smarter use of sales and product data.',
    href: '/contact',
  },
  {
    name: 'Non-profit',
    image:
      'https://ik.imagekit.io/7wqj9br0b/PTKCS/Services/Industries/Non%20Profit%20industry.webp',
    description:
      'PT KCS helps non-profits put AI to work for their missions, from streamlining operations to improving how they engage and support communities.',
    href: '/contact',
  },
];

export default function IndustrySolutions() {
  const carouselRef = useRef(null);

  const moveCarousel = (direction) => {
    const carousel = carouselRef.current;
    const firstCard = carousel?.firstElementChild;

    if (!carousel || !firstCard) return;

    const cardWidth = firstCard.getBoundingClientRect().width;
    const gap = Number.parseFloat(window.getComputedStyle(carousel).columnGap) || 0;
    const distance = cardWidth + gap;
    const maxScroll = carousel.scrollWidth - carousel.clientWidth;

    if (direction === 'next') {
      carousel.scrollTo({
        left: carousel.scrollLeft >= maxScroll - 1 ? 0 : carousel.scrollLeft + distance,
        behavior: 'smooth',
      });
      return;
    }

    carousel.scrollTo({
      left: carousel.scrollLeft <= 1 ? maxScroll : Math.max(0, carousel.scrollLeft - distance),
      behavior: 'smooth',
    });
  };

  return (
    <section className="px-5 py-10 sm:px-6 sm:py-12 lg:px-10" style={{ backgroundColor: '#F7F7F7' }}>
      <div className="mx-auto max-w-7xl">
        <h2
          className="text-center text-2xl font-medium leading-tight tracking-tight sm:text-3xl xl:text-4xl"
          style={{ color: '#000000' }}
        >
          Industries We Serve
        </h2>

        <div
          ref={carouselRef}
          className="mt-9 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mt-11"
          aria-label="Industries we serve"
        >
          {industries.map((industry) => (
            <article
              key={industry.name}
              className="flex min-h-[376px] w-full flex-none snap-start flex-col overflow-hidden rounded-md sm:basis-[calc(50%-8px)] lg:basis-[calc(25%-12px)]"
              style={{ backgroundColor: '#FFFFFF' }}
            >
              <img
                src={industry.image}
                alt={industry.name}
                className="h-40 w-full shrink-0 object-cover"
                loading="lazy"
              />
              <div className="flex flex-1 flex-col p-4 sm:p-5">
                <h3
                  className="text-lg font-medium leading-snug xl:text-xl"
                  style={{ color: '#000000' }}
                >
                  {industry.name}
                </h3>
                <p className="mt-3 flex-1 text-sm font-normal leading-relaxed xl:text-base" style={{ color: '#475569' }}>
                  {industry.description}
                </p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => moveCarousel('previous')}
            aria-label="Previous industries"
            className="flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ backgroundColor: '#666666', color: '#FFFFFF' }}
          >
            <ChevronLeft size={22} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => moveCarousel('next')}
            aria-label="Next industries"
            className="flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ backgroundColor: '#666666', color: '#FFFFFF' }}
          >
            <ChevronRight size={22} aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}
